import * as React from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import type { MessageFormatElement } from '@formatjs/icu-messageformat-parser';
import { withAsync } from '@helpcenter/with-async';
import { onIntlNextError } from 'providers';
import { IntlProvider } from 'react-intl-next';
import { lazyForPaint } from 'react-loosely-lazy';
import type { State } from 'state';
import { getLocale } from 'state/selectors/user';
import { importLocaleMessages, getNormalizedLocale } from '@atlassian/help-center-common-util/editor-translation-util';
import { getLanguageFromLocale } from '@atlassian/help-center-common-util/locale-data';
import { promiseWithRetries } from '@atlassian/help-center-common-util/retry';

export interface ConnectedProps {
    locale: string;
}

// eslint-disable-next-line @typescript-eslint/consistent-type-imports
type RichTextEditorProps = import('./rich-text-editor-v2').RichTextEditorProps;

// Re-export types for external use
export type { MediaContext, RichTextEditorActions } from './rich-text-editor-v2';

/**
 *  (Currently) the editor supports 2 rendering modes in the Portal, it can render either as a comment box or as a field in the request create form.
 *  Both cases can be see as being "semi-uncontrolled" components.
 *
 *  In the case of the comment box, we copy the ADF document to redux UI state on submit
 *  so we can re-populate the editor if the request fails.
 *
 *  In the case of the field, we update Formik's internal state as the user types, and use that value when submitting the form.
 *
 */
const AsyncRichTextEditor = (props: RichTextEditorProps) => {
    const prevProps = React.useRef(props);
    React.useEffect(() => {
        prevProps.current = props;
    });

    const AsyncEditor: React.ComponentType<RichTextEditorProps> = React.useMemo(
        () =>
            withAsync({
                loader: lazyForPaint(() => import(/* webpackChunkName: "rich-text-editor-v2" */ './hocs'), {
                    ssr: false,
                }),
                LoadingSkeleton: props.LoadingComponent,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.editorType]
    );

    return <AsyncEditor {...props} />;
};

const EditorWithTranslations: React.ComponentType<RichTextEditorProps & ConnectedProps> = React.memo(
    function EditorWithTranslationsV2(ownProps) {
        const { locale, LoadingComponent = () => null } = ownProps;
        const [intlMessages, setIntlMessages] = React.useState<
            Record<string, string> | Record<string, MessageFormatElement[]>
        >();
        const [error, setError] = React.useState<Error | undefined>();

        React.useEffect(() => {
            promiseWithRetries(() => importLocaleMessages(locale))
                .then((loaded) => {
                    setIntlMessages(loaded);
                })
                .catch((err) => {
                    setError(err);
                });
        }, [locale]);

        if (!intlMessages) {
            return <LoadingComponent error={error} isLoading={!error} />;
        }

        return (
            <IntlProvider messages={intlMessages} locale={getLanguageFromLocale(locale)} onError={onIntlNextError}>
                <AsyncRichTextEditor {...ownProps} />
            </IntlProvider>
        );
    }
);

export const LocalizedAsyncRichTextEditor = (props: RichTextEditorProps & ConnectedProps) => {
    const { locale, LoadingComponent = () => null } = props;

    // If the users locale is english, which is the editors default locale, we can avoid requiring the locale unnecessarily
    if (locale === 'en') {
        return <AsyncRichTextEditor {...props} LoadingComponent={LoadingComponent} />;
    }

    return <EditorWithTranslations {...props} LoadingComponent={LoadingComponent} />;
};

const selector = createSelector(
    (state: State) => getLocale(state),
    (locale) => ({ locale: getNormalizedLocale(locale) })
);

export default connect(selector)(LocalizedAsyncRichTextEditor);
